import { realDataStart } from '../state/realData.ts';
import { snowProfile, type ProfileLayer } from '../sim/profile.ts';
import { GRAIN_LABELS, type Grain } from '../sim/snowpack.ts';
import { selectedCell, snowpack } from '../state/store.ts';
import {
  formatAspect,
  formatNumber,
  formatTemperature,
  formatTimelineClock,
  skierClass,
} from './format.ts';
import { GRAIN_COLORS } from './grainColors.ts';

/** Bar width (px) per hand hardness, F (softest) narrowest to K (hardest) widest — the standard
 * snow-profile convention. */
const HARDNESS_WIDTH: Record<ProfileLayer['hardness'], number> = {
  F: 26,
  '4F': 46,
  '1F': 66,
  P: 86,
  K: 106,
};

const SVG_WIDTH = 150;
const SVG_HEIGHT = 260;
const MARGIN_TOP = 12;
const MARGIN_BOTTOM = 20;
const BAR_X = 34;

function formatAge(hours: number): string {
  return hours < 48 ? `${formatNumber(hours, 0)} h` : `${formatNumber(hours / 24, 1)} d`;
}

interface AxisTick {
  cm: number;
  y: number;
}

/** Snow-pit style profile of the selected cell, drawn as an SVG: bar width = hand hardness,
 * colour = grain, wet layers hatched, the critical layer outlined in red with its SK38 value. A
 * dismissible floating card over the 3D view. */
export function SnowProfilePanel() {
  const sp = snowpack.value;
  const cell = selectedCell.value;
  if (!sp || cell === null || cell < 0 || cell >= sp.columns.count.length) return null;

  const profile = snowProfile(sp, cell);
  const close = () => (selectedCell.value = null);

  const hsCm = Math.max(profile.hs * 100, 1);
  const chartH = SVG_HEIGHT - MARGIN_TOP - MARGIN_BOTTOM;
  const pxPerCm = chartH / hsCm;
  const yFor = (cm: number): number => SVG_HEIGHT - MARGIN_BOTTOM - cm * pxPerCm;

  const ticks: AxisTick[] = [0, hsCm / 2, hsCm].map((cm) => ({ cm, y: yFor(cm) }));
  const critical = profile.critical >= 0 ? profile.layers[profile.critical] : undefined;

  return (
    <div class="snow-profile-panel">
      <div class="snow-profile-header">
        <h3>Snow profile</h3>
        <button type="button" class="icon-button" onClick={close} aria-label="Close profile">
          ×
        </button>
      </div>
      {/* The profile is always the exact per-cell state at the evaluation hour — it doesn't
          follow the timeline scrubber the way the depth/stability snapshots do, so it's labelled
          with the hour it actually describes. */}
      <p class="hint">At {formatTimelineClock(sp.evaluationHour, realDataStart())}</p>
      <dl class="info-list">
        <dt>Elevation</dt>
        <dd>{formatNumber(profile.elevation)} m</dd>
        <dt>Slope</dt>
        <dd>{formatNumber(profile.slope, 1)}°</dd>
        <dt>Aspect</dt>
        <dd>{formatAspect(profile.aspect)}</dd>
        <dt>Air temp.</dt>
        <dd>{formatTemperature(profile.airTemp, 1)} °C</dd>
        <dt>HS</dt>
        <dd>{formatNumber(profile.hs * 100)} cm</dd>
        <dt>Avalanche problem</dt>
        <dd>{profile.problemLabel}</dd>
      </dl>

      {profile.layers.length === 0 ? (
        <p class="hint">No snow at this point.</p>
      ) : (
        <>
          <div class="snow-profile-body">
            <svg
              width={SVG_WIDTH}
              height={SVG_HEIGHT}
              class="snow-profile-svg"
              role="img"
              aria-label="Snow profile"
            >
              <defs>
                <pattern
                  id="snow-profile-wet"
                  width={4}
                  height={4}
                  patternTransform="rotate(45)"
                  patternUnits="userSpaceOnUse"
                >
                  <line x1={0} y1={0} x2={0} y2={4} stroke="#2b6fd1" stroke-width={1.5} />
                </pattern>
              </defs>
              <line
                x1={BAR_X - 4}
                y1={MARGIN_TOP}
                x2={BAR_X - 4}
                y2={SVG_HEIGHT - MARGIN_BOTTOM}
                class="snow-profile-axis"
              />
              {ticks.map((t) => (
                <g key={t.cm}>
                  <line x1={BAR_X - 7} y1={t.y} x2={BAR_X - 4} y2={t.y} class="snow-profile-axis" />
                  <text x={BAR_X - 9} y={t.y} class="snow-profile-axis-label" text-anchor="end">
                    {formatNumber(t.cm, 0)}
                  </text>
                </g>
              ))}
              {profile.layers.map((l, i) => {
                const width = HARDNESS_WIDTH[l.hardness];
                const yTop = yFor(l.top * 100);
                const yBottom = yFor(l.bottom * 100);
                const height = Math.max(1.5, yBottom - yTop);
                const wet = l.lwcPct > 0.5;
                return (
                  <g key={i}>
                    <rect
                      x={BAR_X}
                      y={yTop}
                      width={width}
                      height={height}
                      fill={GRAIN_COLORS[l.grain]}
                      stroke={l.critical ? '#d9271e' : '#2b2f33'}
                      stroke-width={l.critical ? 2.5 : 1}
                    />
                    {wet ? (
                      <rect
                        x={BAR_X}
                        y={yTop}
                        width={width}
                        height={height}
                        fill="url(#snow-profile-wet)"
                      />
                    ) : null}
                    {l.critical ? (
                      <text
                        x={BAR_X + width + 6}
                        y={(yTop + yBottom) / 2}
                        class="snow-profile-critical-label"
                      >
                        SK38 {formatNumber(l.skier, 2)}
                      </text>
                    ) : null}
                  </g>
                );
              })}
            </svg>
            <table class="snow-profile-table">
              <thead>
                <tr>
                  <th>Grain</th>
                  <th>cm</th>
                  <th>kg/m³</th>
                  <th>Age</th>
                  <th>LWC</th>
                  <th title="Shear strength of the layer, or of its bond to the younger layer on top if that is weaker">
                    Strength kPa
                  </th>
                  <th title="Skier stability index with this layer as the failure layer">SK38</th>
                </tr>
              </thead>
              <tbody>
                {profile.layers.map((l, i) => (
                  <tr key={i} class={l.critical ? 'critical' : ''}>
                    <td class="snow-profile-grain-cell">
                      <span
                        class="grain-swatch"
                        style={{ backgroundColor: GRAIN_COLORS[l.grain] }}
                      />
                      {l.grainLabel}
                    </td>
                    <td>{formatNumber(l.thickness * 100)}</td>
                    <td>{formatNumber(l.density)}</td>
                    <td>{formatAge(l.ageHours)}</td>
                    <td>{formatNumber(l.lwcPct, 1)}%</td>
                    <td>{formatNumber(l.strengthKPa, 2)}</td>
                    <td>{l.hasSlab ? formatNumber(l.skier, 2) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p class="hint">Bar width = hand hardness (F softest … K hardest).</p>
          {critical ? (
            <p class="snow-profile-critical-note">
              Critical layer: {critical.grainLabel} at {formatNumber(critical.top * 100)} cm — SK38{' '}
              {formatNumber(critical.skier, 2)} ({skierClass(critical.skier)})
            </p>
          ) : null}
          <div class="grain-legend">
            {(Object.keys(GRAIN_LABELS).map(Number) as Grain[]).map((g) => (
              <span key={g} class="legend-item">
                <span class="legend-swatch" style={{ backgroundColor: GRAIN_COLORS[g] }} />
                {GRAIN_LABELS[g]}
              </span>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
